import { apiService } from '../../../components/api/apiService';
import type { GovernanceLevel, GovernancePolicyRecord, PolicyStatus } from './components/governanceStore';
import { logDataModels } from './dataModelsLogger';
import { emptyTarget, summarizeAppliesTo } from './governance/engine';
import { documentFromApi } from './governance/policyInspect';
import type { GovernancePolicy, PolicyAsset, PolicyDocument, PolicyScope, PolicyType } from './governance/types';
import { POLICY_TYPE_KIND } from './governance/types';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/data-model-policies';

export type ApiPolicyLevel = 'workspace' | 'catalog' | 'schema';

export interface ApiPolicyRecord {
  id: string;
  level: ApiPolicyLevel;
  workspaceId: string;
  catalogId?: string;
  schemaId?: string;
  ownerId?: string;
  name: string;
  description?: string;
  policyKey?: string;
  type?: PolicyType;
  status?: PolicyStatus;
  flag?: GovernancePolicyRecord['flag'];
  target?: GovernancePolicy['target'];
  rules?: GovernancePolicy['rules'];
  advancedExpression?: string;
  version?: number;
  document?: PolicyDocument;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  policyServerSynced?: boolean;
}

export interface PolicyWriteBody {
  id?: string;
  level: ApiPolicyLevel;
  workspaceId: string;
  catalogId?: string;
  schemaId?: string;
  name: string;
  description?: string;
  policyKey?: string;
  type: PolicyType;
  status: PolicyStatus;
  flag?: GovernancePolicyRecord['flag'];
  target: GovernancePolicy['target'];
  rules: GovernancePolicy['rules'];
  advancedExpression?: string;
  version?: number;
  document?: PolicyDocument;
}

function serviceErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as {
    response?: { data?: { error?: { message?: string }; message?: string } };
    message?: string;
  };
  return axiosErr.response?.data?.error?.message || axiosErr.response?.data?.message || axiosErr.message || fallback;
}

function asList(data: unknown): ApiPolicyRecord[] {
  const record = data && typeof data === 'object' ? (data as { policies?: unknown }) : {};
  return Array.isArray(record.policies) ? (record.policies as ApiPolicyRecord[]) : [];
}

export function apiLevelFromUi(level: GovernanceLevel): ApiPolicyLevel {
  if (level === 'model') return 'catalog';
  if (level === 'asset') return 'schema';
  return 'workspace';
}

export function uiLevelFromApi(level: ApiPolicyLevel): GovernanceLevel {
  if (level === 'catalog') return 'model';
  if (level === 'schema') return 'asset';
  return 'vault';
}

export function scopeFromApi(row: ApiPolicyRecord): PolicyScope {
  if (row.level === 'catalog') {
    return { level: 'MODEL', vaultId: row.workspaceId, modelId: row.catalogId };
  }
  if (row.level === 'schema') {
    return { level: 'ASSET', vaultId: row.workspaceId, modelId: row.catalogId, assetId: row.schemaId };
  }
  return { level: 'VAULT', vaultId: row.workspaceId };
}

export function detailFromApi(row: ApiPolicyRecord): GovernancePolicy {
  const status = row.status === 'Enabled' ? 'ACTIVE' : row.status === 'Disabled' ? 'INACTIVE' : 'DRAFT';
  const document = documentFromApi(row.document);
  return {
    id: row.id,
    name: row.name,
    description: row.description || '',
    type: row.type || 'ROW_LEVEL',
    status,
    scope: scopeFromApi(row),
    target: row.target && typeof row.target === 'object' ? { ...emptyTarget(), ...row.target } : emptyTarget(),
    rules: Array.isArray(row.rules) ? row.rules : [],
    advancedExpression: row.advancedExpression || '',
    policyKey: row.policyKey || '',
    generateId: Boolean(document?.meta?.generatedId),
    version: row.version || 1,
    createdAt: row.createdAt || '',
    updatedAt: row.updatedAt || '',
    ...(document ? { document } : {}),
  };
}

export function recordFromApi(row: ApiPolicyRecord, assets: PolicyAsset[] = []): GovernancePolicyRecord {
  const detail = detailFromApi(row);
  const level = uiLevelFromApi(row.level);
  const ownerId = row.ownerId || (level === 'vault' ? row.workspaceId : level === 'model' ? row.catalogId || '' : row.schemaId || '');
  return {
    id: row.id,
    level,
    ownerId,
    name: row.name,
    description: row.description || 'Policy',
    kind: POLICY_TYPE_KIND[detail.type],
    flag: row.flag === 'mandatory' || row.flag === 'override' ? row.flag : 'standard',
    appliesTo: summarizeAppliesTo(detail, assets),
    status: row.status === 'Enabled' || row.status === 'Disabled' ? row.status : 'Draft',
    updated: row.updatedAt || 'Just now',
    actor: row.createdBy || 'You',
    persisted: true,
    policyServerSynced: row.policyServerSynced,
    detail,
  };
}

export function writeFromDetail(
  policy: GovernancePolicy,
  status: PolicyStatus,
  flag: GovernancePolicyRecord['flag'] = 'standard',
): PolicyWriteBody {
  const scope = policy.scope;
  const level = scope.level === 'MODEL' ? 'catalog' : scope.level === 'ASSET' ? 'schema' : 'workspace';
  return {
    id: policy.id || undefined,
    level,
    workspaceId: scope.vaultId,
    catalogId: scope.modelId || undefined,
    schemaId: scope.assetId || undefined,
    name: policy.name,
    description: policy.description || '',
    policyKey: policy.policyKey,
    type: policy.type,
    status,
    flag,
    target: policy.target,
    rules: policy.rules,
    advancedExpression: policy.advancedExpression,
    version: policy.version || 1,
    ...(policy.document ? { document: policy.document } : {}),
  };
}

export async function listPolicies(): Promise<ApiPolicyRecord[]> {
  logDataModels('app', 'policies_load');
  try {
    const data = await apiService.get(BASE, {}, CONNECTION);
    const rows = asList(data);
    logDataModels('app', 'policies_load_ok', { count: rows.length });
    return rows;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to load policies');
    logDataModels('error', 'policies_load_failed', { message });
    throw new Error(message);
  }
}

export async function createPolicy(body: PolicyWriteBody): Promise<ApiPolicyRecord> {
  logDataModels('app', 'policy_create', { name: body.name, level: body.level, workspaceId: body.workspaceId });
  try {
    const data = (await apiService.post(BASE, body, CONNECTION)) as ApiPolicyRecord;
    logDataModels('app', 'policy_create_ok', { id: data?.id, level: data?.level });
    return data;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to create policy');
    logDataModels('error', 'policy_create_failed', { message });
    throw new Error(message);
  }
}

export async function updatePolicy(id: string, body: PolicyWriteBody): Promise<ApiPolicyRecord> {
  logDataModels('app', 'policy_update', { id, status: body.status });
  try {
    const data = (await apiService.put(`${BASE}/${encodeURIComponent(id)}`, { ...body, id }, CONNECTION)) as ApiPolicyRecord;
    logDataModels('app', 'policy_update_ok', { id });
    return data;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to update policy');
    logDataModels('error', 'policy_update_failed', { id, message });
    throw new Error(message);
  }
}

export async function deletePolicy(id: string): Promise<boolean> {
  logDataModels('app', 'policy_delete', { id });
  try {
    const data = (await apiService.delete(`${BASE}/${encodeURIComponent(id)}`, CONNECTION)) as { opaSynced?: boolean };
    const synced = Boolean(data?.opaSynced);
    logDataModels('app', 'policy_delete_ok', { id, opaSynced: synced });
    return synced;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to delete policy');
    logDataModels('error', 'policy_delete_failed', { id, message });
    throw new Error(message);
  }
}
